import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useShop } from "../contexts/ShopContext";
import { useAuth } from "../contexts/AuthContext";
import api from "../api";
import SEO from "../components/SEO";
import { useI18n } from "../contexts/I18nContext";
import AddressSearch from "../components/AddressSearch";
import NaverPayButton from "../components/NaverPayButton";
import { getCartProductTitle } from "../utils/productDisplay";

function getProductName(item, fallback) {
  return getCartProductTitle(item, fallback || "Product");
}

function getProductImage(item) {
  return item.product.image_url || null;
}

function getProductPrice(item) {
  const p = item.product;
  return p.price || p.rental_price || null;
}

function getItemDeliveryFee(item) {
  const total = Number(item.delivery_fee_total || 0);
  if (total > 0) return total;
  return Number(item.delivery_fee_per_unit || 0) * Number(item.quantity || 0);
}

export default function CheckoutPage() {
  const { t } = useI18n();
  const { user } = useAuth();
  const { cartItems, fetchCart, removeFromCart } = useShop();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [removingItemId, setRemovingItemId] = useState(null);

  const [form, setForm] = useState({
    recipient_name: "",
    phone: "",
    postal_code: "",
    address: "",
    address_detail: "",
    company: "",
    note: "",
    payment_method: "card",
    save_address: true,
  });

  const [savedAddresses, setSavedAddresses] = useState([]);
  const [showSaved, setShowSaved] = useState(false);

  const kcpFormRef = useRef(null);
  const kcpOrderRef = useRef(null);

  // KCP 결제창 잔여 오버레이/iframe 정리
  const closeKcpLayer = useCallback(() => {
    try {
      const selectors = [
        "#kcp_layer", "#smart_layer", "#layer_modal", "#layer_iframe",
        "#kcp_spay_layer", "#kcp_overlay", "#kcp_progress",
        'iframe[src*="kcp.co.kr"]', 'iframe[src*="kcp"]',
        ".kcp_layer", ".kcp_modal", ".kcp_overlay", ".kcp_progress",
      ];
      selectors.forEach((sel) => {
        document.querySelectorAll(sel).forEach((el) => {
          try { el.remove(); } catch (_) {}
        });
      });
      // body scroll-lock 해제
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    } catch (_) { /* noop */ }
  }, []);

  const finishKcpLayer = useCallback((closeEvent) => {
    try {
      if (typeof closeEvent === "function") {
        closeEvent();
      }
    } catch (_) { /* noop */ }
    closeKcpLayer();
    window.setTimeout(closeKcpLayer, 120);
  }, [closeKcpLayer]);

  // KCP PC 결제창 콜백
  const handleKcpComplete = useCallback(async (kcpCallbackForm, closeEvent) => {
    const formEl = kcpCallbackForm || kcpFormRef.current || document.forms.order_info;
    if (!formEl) {
      alert("결제 인증 결과를 확인할 수 없습니다");
      finishKcpLayer(closeEvent);
      setLoading(false);
      return;
    }

    const getKcpValue = (name) => {
      const direct = formEl?.[name];
      if (direct && typeof direct === "object" && "value" in direct) {
        return direct.value || "";
      }
      if (direct != null && typeof direct !== "object") {
        return String(direct);
      }
      const hidden = kcpFormRef.current?.elements?.[name] || document.forms.order_info?.elements?.[name];
      return hidden?.value || "";
    };

    const resCd = getKcpValue("res_cd");
    const resMsg = getKcpValue("res_msg");
    // 사용자 취소 또는 인증 실패 → 결제창 닫기
    if (resCd !== "0000") {
      finishKcpLayer(closeEvent);
      // 취소(보통 res_cd 빈값/"XXXX") 시 alert 생략
      const isUserCancel = !resCd || /취소|cancel/i.test(resMsg);
      if (!isUserCancel) {
        alert(`결제 인증 실패: ${resMsg || "알 수 없는 오류"}`);
      }
      setLoading(false);
      return;
    }
    let completionAlert = "";
    try {
      const { data } = await api.post("/api/kcp/approve", {
        enc_data: getKcpValue("enc_data"),
        enc_info: getKcpValue("enc_info"),
        tran_cd: getKcpValue("tran_cd") || "00100000",
        ordr_idxx: getKcpValue("ordr_idxx") || kcpOrderRef.current?.order_number || "",
        good_mny: getKcpValue("good_mny") || String(kcpOrderRef.current?.pay_amount ?? kcpOrderRef.current?.total_amount ?? ""),
      });
      if (data.success) {
        fetchCart();
        navigate("/order-confirm", {
          state: {
            order: {
              order_id: data.order_id,
              order_number: data.order_number,
              total_amount: data.amount,
              payment_method: "card",
              card_name: data.card_name,
              app_no: data.app_no,
              tno: data.tno,
            },
          },
        });
      } else {
        completionAlert = `결제 실패: ${data.res_msg || data.error || "승인 요청이 거절되었습니다"}`;
      }
    } catch (err) {
      completionAlert = err.response?.data?.error || "결제 승인 요청 실패";
    } finally {
      finishKcpLayer(closeEvent);
      setLoading(false);
      if (completionAlert) {
        window.setTimeout(() => alert(completionAlert), 0);
      }
    }
  }, [fetchCart, navigate, finishKcpLayer]);

  // Register KCP callback
  useEffect(() => {
    window.m_Completepayment = (formEl, closeEvent) => {
      handleKcpComplete(formEl, closeEvent);
    };
    return () => { delete window.m_Completepayment; };
  }, [handleKcpComplete]);

  // Pre-fill from user profile
  useEffect(() => {
    if (user) {
      setForm(f => ({
        ...f,
        recipient_name: f.recipient_name || user.name || "",
        phone: f.phone || user.phone || "",
        company: f.company || user.company || "",
      }));
    }
  }, [user]);

  // Fetch saved addresses
  useEffect(() => {
    if (user) {
      api.get("/api/addresses").then(({ data }) => {
        setSavedAddresses(data || []);
        // Auto-fill default address
        const def = (data || []).find(a => a.is_default);
        if (def) {
          setForm(f => ({
            ...f,
            recipient_name: f.recipient_name || def.recipient_name || "",
            phone: f.phone || def.phone || "",
            postal_code: def.postal_code || "",
            address: def.address || "",
            address_detail: def.address_detail || "",
            company: f.company || def.company || "",
          }));
        }
      }).catch(() => {});
    }
  }, [user]);

  const selectSavedAddress = (addr) => {
    setForm(f => ({
      ...f,
      recipient_name: addr.recipient_name || f.recipient_name,
      phone: addr.phone || f.phone,
      postal_code: addr.postal_code || "",
      address: addr.address || "",
      address_detail: addr.address_detail || "",
      company: addr.company || f.company,
    }));
    setShowSaved(false);
  };

  const deleteSavedAddress = async (addrId, e) => {
    e.stopPropagation();
    if (!window.confirm("이 배송지를 삭제하시겠습니까?")) return;
    try {
      await api.delete(`/api/addresses/${addrId}`);
      setSavedAddresses(prev => prev.filter(a => a.id !== addrId));
    } catch {}
  };

  const handleRemoveCartItem = async (itemId) => {
    if (loading || removingItemId) return;
    setRemovingItemId(itemId);
    try {
      await removeFromCart(itemId);
    } finally {
      setRemovingItemId(null);
    }
  };

  if (!user) {
    return (
      <div className="dept-access-page">
        <SEO title={t.checkout_heading} path="/checkout" />
        <div className="dept-access-page__inner">
          <div className="dept-access-page__mark" aria-hidden="true">ORDER</div>
          <div className="dept-access-page__copy">
            <span className="dept-access-page__eyebrow">일광오피스 주문 서비스</span>
            <h1>{t.checkout_loginRequired}</h1>
            <p>배송지 입력과 안전한 결제를 위해 로그인해주세요.</p>
            <div className="dept-access-page__actions">
              <Link to="/login" state={{ from: "/checkout" }} className="dept-access-page__primary">
                {t.checkout_loginBtn}
              </Link>
              <Link to="/cart" className="dept-access-page__secondary">장바구니 보기</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (cartItems.length === 0) {
    return (
      <div className="max-w-[980px] mx-auto px-4 pt-20 pb-16 text-center">
        <SEO title={t.checkout_heading} path="/checkout" />
        <p className="text-gray-500 mb-4">{t.checkout_emptyCart}</p>
        <Link to="/products" className="inline-block px-6 py-3 bg-[#0071e3] text-white rounded-xl">{t.checkout_browse}</Link>
      </div>
    );
  }

  const productTotal = cartItems.reduce((sum, item) => {
    const price = getProductPrice(item);
    return sum + (price ? price * item.quantity : 0);
  }, 0);
  const deliveryTotal = cartItems.reduce((sum, item) => sum + getItemDeliveryFee(item), 0);
  const totalPrice = productTotal + deliveryTotal;
  // 네이버페이(주문형)는 생성형 판매상세(page) 상품만 지원 — 장바구니가 전부 판매상품일 때만 노출
  const naverPayEligible = cartItems.length > 0 && cartItems.every((item) => (
    item.product_type === "page"
    && item.product?.sale_type === "sale"
    && item.product?.mall_type !== "b2b"
    && Number(item.product?.price || 0) > 0
  ));

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!form.recipient_name.trim() || !form.phone.trim() || !form.address.trim()) {
      alert(t.checkout_validation);
      return;
    }
    setLoading(true);

    if (form.payment_method === "card") {
      // KCP 카드결제 flow
      try {
        // 1. 주문 준비 (DB에 pending 주문 생성)
        const { data: orderData } = await api.post("/api/kcp/order-prepare", form);
        kcpOrderRef.current = orderData;

        const isMobile = /Android|iPhone|iPad|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

        if (isMobile) {
          // Mobile: 거래등록 → PayUrl 리다이렉트
          const retUrl = `${window.location.origin}/api/kcp/mobile-return`;
          const payAmount = orderData.pay_amount ?? orderData.total_amount;
          const { data: tradeData } = await api.post("/api/kcp/trade-register", {
            order_number: orderData.order_number,
            total_amount: payAmount,
            good_name: orderData.good_name,
            ret_url: retUrl,
            pay_method: "CARD",
          });

          // Create and submit hidden form to PayUrl
          const mForm = document.createElement("form");
          mForm.method = "POST";
          mForm.action = tradeData.PayUrl;
          const fields = {
            site_cd: orderData.site_cd || "T0000",
            pay_method: tradeData.paymentMethod || "CARD",
            currency: "410",
            shop_name: "일광오피스",
            Ret_URL: retUrl,
            approval_key: tradeData.approval_key,
            PayUrl: tradeData.PayUrl,
            ordr_idxx: orderData.order_number,
            good_name: orderData.good_name,
            good_mny: String(payAmount),
            buyr_name: form.recipient_name,
            buyr_tel2: form.phone,
          };
          for (const [k, v] of Object.entries(fields)) {
            const inp = document.createElement("input");
            inp.type = "hidden";
            inp.name = k;
            inp.value = v;
            mForm.appendChild(inp);
          }
          document.body.appendChild(mForm);
          mForm.submit();
          return; // page will redirect
        } else {
          // PC: KCP_Pay_Execute_Web
          const formEl = kcpFormRef.current;
          if (!formEl) {
            alert("결제 폼 초기화 오류");
            setLoading(false);
            return;
          }
          formEl.site_cd.value = orderData.site_cd || "T0000";
          formEl.site_name.value = "IKOFFICES";
          formEl.pay_method.value = "100000000000"; // 신용카드
          formEl.ordr_idxx.value = orderData.order_number;
          formEl.good_name.value = orderData.good_name;
          formEl.good_mny.value = String(orderData.pay_amount ?? orderData.total_amount);
          formEl.buyr_name.value = form.recipient_name;
          formEl.buyr_tel2.value = form.phone;
          formEl.currency.value = "WON";

          try {
            window.KCP_Pay_Execute_Web(formEl);
          } catch (e) {
            // KCP throws on normal completion
          }
          setLoading(false);
        }
      } catch (err) {
        alert(err.response?.data?.error || "결제 준비 실패");
        setLoading(false);
      }
    } else {
      // 기존 무통장입금 / 후불 flow
      try {
        const { data } = await api.post("/api/orders", form);
        fetchCart();
        navigate("/order-confirm", { state: { order: data } });
      } catch (err) {
        alert(err.response?.data?.error || t.checkout_error);
      } finally {
        setLoading(false);
      }
    }
  };

  const inp = "w-full border border-[#d2d2d7] rounded-xl px-4 py-3 text-[14px] text-[#1d1d1f] placeholder-[#86868b] focus:outline-none focus:ring-2 focus:ring-[#0071e3]/20 focus:border-[#0071e3] transition bg-white";

  return (
    <div className="bg-[#f5f5f7] min-h-screen">
      <SEO title={t.checkout_heading} path="/checkout" />

      <div className="max-w-[980px] mx-auto px-4 pt-10 pb-20">
        <h1 className="text-[28px] md:text-[36px] font-semibold text-[#1d1d1f] tracking-tight mb-8">{t.checkout_heading}</h1>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Left: Shipping Form */}
            <div className="lg:col-span-3 space-y-6">
              {/* Shipping Info */}
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100/80">
                <h2 className="text-[17px] font-semibold text-[#1d1d1f] mb-5 flex items-center gap-2">
                  <svg className="w-5 h-5 text-[#0071e3]" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.149-.504 1.174-1.125a59.769 59.769 0 00-.165-4.397l-.398-3.179c-.145-1.164-1.138-2.024-2.311-2.024h-2.5V5.625c0-.621-.504-1.125-1.125-1.125H5.25c-.621 0-1.125.504-1.125 1.125v12.75m17.25 0h.375a1.125 1.125 0 001.125-1.125V14.25"/></svg>
                  {t.checkout_delivery}
                </h2>

                {/* 저장된 배송지 */}
                {savedAddresses.length > 0 && (
                  <div className="mb-4">
                    <button type="button" onClick={() => setShowSaved(!showSaved)}
                      className="flex items-center gap-2 text-[13px] font-medium text-[#0071e3] hover:text-[#005bb5] transition">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0111.186 0z" />
                      </svg>
                      저장된 배송지 ({savedAddresses.length})
                      <svg className={`w-3 h-3 transition-transform ${showSaved ? "rotate-180" : ""}`} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {showSaved && (
                      <div className="mt-2 space-y-2 max-h-[200px] overflow-y-auto">
                        {savedAddresses.map((addr) => (
                          <div key={addr.id}
                            onClick={() => selectSavedAddress(addr)}
                            className="flex items-center justify-between p-3 rounded-xl border border-[#d2d2d7] hover:border-[#0071e3] hover:bg-[#0071e3]/5 cursor-pointer transition group">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <span className="text-[13px] font-semibold text-[#1d1d1f]">{addr.label || "배송지"}</span>
                                {addr.is_default ? <span className="text-[10px] bg-[#0071e3] text-white px-1.5 py-0.5 rounded-full">기본</span> : null}
                              </div>
                              <p className="text-[12px] text-[#6e6e73] mt-0.5 truncate">
                                {addr.recipient_name} · {addr.phone}
                              </p>
                              <p className="text-[12px] text-[#86868b] truncate">
                                {addr.postal_code ? `(${addr.postal_code}) ` : ""}{addr.address} {addr.address_detail}
                              </p>
                            </div>
                            <button type="button" onClick={(e) => deleteSavedAddress(addr.id, e)}
                              className="ml-2 p-1.5 text-[#86868b] hover:text-red-500 opacity-0 group-hover:opacity-100 transition">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[12px] font-medium text-[#6e6e73] mb-1.5">{t.checkout_recipient}</label>
                      <input name="recipient_name" value={form.recipient_name} onChange={handleChange} required className={inp} placeholder={t.checkout_recipientPh} />
                    </div>
                    <div>
                      <label className="block text-[12px] font-medium text-[#6e6e73] mb-1.5">{t.checkout_phone}</label>
                      <input name="phone" value={form.phone} onChange={handleChange} required className={inp} placeholder="010-0000-0000" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[12px] font-medium text-[#6e6e73] mb-1.5">{t.checkout_company}</label>
                    <input name="company" value={form.company} onChange={handleChange} className={inp} placeholder={t.checkout_companyPh} />
                  </div>
                  <div>
                    <AddressSearch
                      value={form.address}
                      label={t.checkout_address}
                      placeholder={t.checkout_addressPh || "도로명 / 지번 주소 검색"}
                      required
                      useDaumDirect
                      inputClassName="border-[#d2d2d7] rounded-xl px-4 py-3 text-[14px] text-[#1d1d1f] placeholder-[#86868b] focus:outline-none focus:ring-2 focus:ring-[#0071e3]/20 focus:border-[#0071e3] bg-white"
                      onSelect={({ address, postalCode }) => {
                        setForm(f => ({ ...f, address, postal_code: postalCode || f.postal_code }));
                      }}
                      onChange={(v) => setForm(f => ({...f, address: v}))}
                    />
                  </div>
                  <div>
                    <label className="block text-[12px] font-medium text-[#6e6e73] mb-1.5">{t.checkout_detailAddr}</label>
                    <input name="address_detail" value={form.address_detail} onChange={handleChange} className={inp} placeholder={t.checkout_detailAddrPh} />
                  </div>
                  <div>
                    <label className="block text-[12px] font-medium text-[#6e6e73] mb-1.5">{t.checkout_memo}</label>
                    <textarea name="note" value={form.note} onChange={handleChange} rows={2} className={inp + " resize-none"} placeholder={t.checkout_memoPh} />
                  </div>

                  {/* 배송지 저장 체크박스 */}
                  <label className="flex items-center gap-2.5 cursor-pointer select-none">
                    <input type="checkbox" checked={form.save_address}
                      onChange={(e) => setForm({...form, save_address: e.target.checked})}
                      className="w-4 h-4 rounded border-[#d2d2d7] text-[#0071e3] focus:ring-[#0071e3]/30 accent-[#0071e3]" />
                    <span className="text-[13px] text-[#1d1d1f]">이 배송지 저장 (다음에 바로 사용)</span>
                  </label>
                </div>
              </div>

              {/* Payment Method */}
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100/80">
                <h2 className="text-[17px] font-semibold text-[#1d1d1f] mb-5 flex items-center gap-2">
                  <svg className="w-5 h-5 text-[#0071e3]" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z"/></svg>
                  {t.checkout_payMethod}
                </h2>
                <div className="grid grid-cols-1 gap-3">
                  {[
                    { value: "card", label: t.checkout_cardPay, desc: "" },
                  ].map(m => (
                    <label key={m.value}
                      className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition ${
                        form.payment_method === m.value
                          ? "border-[#0071e3] bg-[#0071e3]/5"
                          : "border-[#d2d2d7] hover:border-[#86868b]"
                      }`}>
                      <input type="radio" name="payment_method" value={m.value}
                        checked={form.payment_method === m.value}
                        onChange={handleChange}
                        className="mt-1 accent-[#0071e3]"
                      />
                      <div>
                        <p className="text-[14px] font-semibold text-[#1d1d1f] mt-0.5">{m.label}</p>
                        <p className="text-[11px] text-[#86868b]">{m.desc}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Right: Order Summary */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100/80 sticky top-20">
                <h2 className="text-[17px] font-semibold text-[#1d1d1f] mb-4">{t.checkout_orderItems}</h2>

                <div className="space-y-3 max-h-[350px] overflow-y-auto pr-1">
                  {cartItems.map((item) => (
                    <div key={item.id} className="flex gap-3 pb-3 border-b border-gray-100 last:border-0">
                      <div className="w-14 h-14 shrink-0 bg-[#f5f5f7] rounded-lg flex items-center justify-center overflow-hidden">
                        {getProductImage(item) ? (
                          <img src={getProductImage(item)} alt="" className="w-full h-full object-contain" />
                        ) : (
                          <span className="text-[11px] font-bold text-gray-400">상품</span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start gap-2">
                          <p className="text-[13px] font-medium text-[#1d1d1f] leading-snug break-words flex-1 min-w-0">
                            {item.product?.product_code && <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 mr-1 align-middle"><svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z" /><path strokeLinecap="round" strokeLinejoin="round" d="M6 6h.008v.008H6V6z" /></svg>{item.product.product_code}</span>}
                            {getProductName(item, t.checkout_product)}
                          </p>
                          <button
                            type="button"
                            onClick={() => handleRemoveCartItem(item.id)}
                            disabled={loading || removingItemId === item.id}
                            className="shrink-0 w-7 h-7 rounded-full text-[#86868b] hover:text-red-600 hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed transition flex items-center justify-center"
                            title={t.cart_remove || "삭제"}
                            aria-label={`${getProductName(item, t.checkout_product)} ${t.cart_remove || "삭제"}`}
                          >
                            {removingItemId === item.id ? (
                              <span className="w-3.5 h-3.5 rounded-full border-2 border-current border-t-transparent animate-spin" />
                            ) : (
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            )}
                          </button>
                        </div>
                        <div className="flex items-center justify-between mt-1">
                          <span className="text-[11px] text-[#86868b]">{t.checkout_qty} {item.quantity}</span>
                          {getProductPrice(item) && (
                            <span className="text-[13px] font-semibold text-[#1d1d1f]">
                              {(getProductPrice(item) * item.quantity).toLocaleString()}{t.checkout_won}
                            </span>
                          )}
                        </div>
                        {getItemDeliveryFee(item) > 0 && (
                          <p className="text-[11px] text-[#86868b] mt-1">
                            배송비 {getItemDeliveryFee(item).toLocaleString()}{t.checkout_won}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="border-t border-gray-200 mt-4 pt-4 space-y-2">
                  <div className="flex justify-between text-[13px] text-[#6e6e73]">
                    <span>{t.checkout_itemCount}</span>
                    <span>{cartItems.length}{t.checkout_count}</span>
                  </div>
                  <div className="flex justify-between text-[13px] text-[#6e6e73]">
                    <span>{t.checkout_shipping}</span>
                    <span>{deliveryTotal > 0 ? `${deliveryTotal.toLocaleString()}${t.checkout_won}` : t.checkout_free}</span>
                  </div>
                  {totalPrice > 0 && (
                    <div className="flex justify-between text-[18px] font-bold text-[#1d1d1f] pt-3 border-t border-gray-200 mt-2">
                      <span>{t.checkout_total}</span>
                      <span className="text-[#0071e3]">{totalPrice.toLocaleString()}{t.checkout_won}</span>
                    </div>
                  )}
                  {totalPrice === 0 && (
                    <div className="flex justify-between text-[15px] font-bold text-[#1d1d1f] pt-3 border-t border-gray-200 mt-2">
                      <span>{t.checkout_total}</span>
                      <span className="text-[#86868b]">{t.checkout_quoteGuide}</span>
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full mt-6 py-3.5 bg-[#0071e3] text-white rounded-xl font-semibold text-[15px] hover:bg-[#005bb5] transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? t.checkout_processing : t.checkout_placeOrder}
                </button>

                {naverPayEligible && (
                  <>
                    <div className="flex items-center gap-3 my-4">
                      <div className="flex-1 h-px bg-gray-200" />
                      <span className="text-[12px] text-[#86868b]">또는</span>
                      <div className="flex-1 h-px bg-gray-200" />
                    </div>
                    <NaverPayButton mode="cart" />
                  </>
                )}

                <p className="text-[12px] text-center text-[#6e6e73] mt-4">주문 전 <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-[#0071e3] underline">이용약관</a>, <a href="/policy" target="_blank" rel="noopener noreferrer" className="text-[#0071e3] underline">교환·반품 정책</a> 및 <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-[#0071e3] underline">개인정보처리방침</a>을 확인해 주세요.</p>

                <Link to="/cart" className="block text-center text-[13px] text-[#0071e3] mt-3 hover:underline">
                  {t.checkout_backToCart}
                </Link>
              </div>
            </div>
          </div>
        </form>

        {/* KCP PC 결제용 숨김 폼 */}
        <form ref={kcpFormRef} name="order_info" style={{ display: "none" }}>
          <input type="hidden" name="site_cd" />
          <input type="hidden" name="site_name" />
          <input type="hidden" name="pay_method" />
          <input type="hidden" name="ordr_idxx" />
          <input type="hidden" name="good_name" />
          <input type="hidden" name="good_mny" />
          <input type="hidden" name="buyr_name" />
          <input type="hidden" name="buyr_tel2" />
          <input type="hidden" name="currency" />
          <input type="hidden" name="res_cd" />
          <input type="hidden" name="res_msg" />
          <input type="hidden" name="enc_data" />
          <input type="hidden" name="enc_info" />
          <input type="hidden" name="tran_cd" />
        </form>
      </div>
    </div>
  );
}
